// documentBranding — drive the browser tab (title + favicon) from the resolved
// tenant branding. index.html ships a static default ("Rheora" + the generic
// /favicon.svg); once orgContext resolves a tenant we override both so the tab
// reflects the customer, matching the in-app lettermark logo.
//
// The favicon is generated from the SAME lettermark the CRM shell renders
// (monogram + accent colour) as an inline SVG data-URI — no image asset upload
// exists on the branding entity today. Pure helpers so they unit-test without a
// browser; the DOM mutation lives in applyDocumentBranding.

import type { ResolvedOrg } from './orgContext';

/** Static defaults declared in index.html — restored when no tenant resolves. */
const DEFAULT_TITLE = 'Rheora';
const DEFAULT_FAVICON = '/favicon.svg';

/** First grapheme of the initials, used as the favicon monogram. */
function monogramOf(org: ResolvedOrg | null): string {
  const initials = org?.branding?.initials;
  if (initials == null || initials === '') return '';
  return [...initials][0] ?? '';
}

/** Tab title: the tenant display/brand name, else the static default. */
export function documentTitleFor(org: ResolvedOrg | null): string {
  const name = org?.branding?.name ?? org?.displayName;
  return name != null && name !== '' ? name : DEFAULT_TITLE;
}

/**
 * A monogram favicon as an SVG `data:` URI — a rounded tile in the tenant accent
 * colour with the initial in white. Returns the static default when the tenant
 * ships no lettermark.
 */
export function faviconFor(org: ResolvedOrg | null): string {
  const monogram = monogramOf(org);
  if (monogram === '') return DEFAULT_FAVICON;
  const accent = org?.branding?.accentColor ?? '#a8392b';
  // Escape the two chars that can break in an unencoded SVG data-URI.
  const safeMono = monogram.replace(/&/g, '&amp;').replace(/</g, '&lt;');
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">` +
    `<rect width="32" height="32" rx="7" fill="${accent}"/>` +
    `<text x="16" y="23" text-anchor="middle" fill="#ffffff" ` +
    `font-family="system-ui,Segoe UI,Arial,sans-serif" font-size="19" font-weight="700">` +
    `${safeMono}</text></svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

/**
 * Apply the resolved tenant branding to the live document (tab title + favicon
 * link). No-op-safe on a server (guards `document`). Idempotent.
 */
export function applyDocumentBranding(org: ResolvedOrg | null): void {
  if (typeof document === 'undefined') return;

  document.title = documentTitleFor(org);

  const href = faviconFor(org);
  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (link === null) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  // A generated monogram is an SVG data-URI; keep the type hint consistent.
  link.type = 'image/svg+xml';
  link.href = href;
}
